import {
  type AiManagedGroupCreateInput,
  type AiManagedGroupEligibleCampaign,
  type AiManagedGroupPolicy,
  resolveExecutionModeV1,
} from "@adpilot/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
  CandidateAiFilter,
  CandidateDeliveryFilter,
  CandidatePortfolioFilter,
  CandidateSortDirection,
  CandidateSortField,
} from "@/components/managed-group/ManagedGroupCandidatePicker";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  createManagedGroup,
  fetchEligibleManagedGroupCampaigns,
  fetchManagedGroupDiagnostics,
  fetchManagedGroupPortfolioOptions,
} from "@/lib/managed-group-api";
import { fetchSystemAiPolicyForShop } from "@/lib/settings-api";
import { useAuthStore } from "@/stores/auth";
import { CAMPAIGN_PAGE_SIZE } from "./campaign-selection";
import {
  canReuseManagedGroupDiagnosticsPlaceholder,
  managedGroupDiagnosticsQueryKey,
} from "./managed-group-diagnostics-query";

interface UseCreateManagedGroupWizardOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Wizard state, server queries, and the create mutation behind `CreateManagedGroupDialog`. */
export function useCreateManagedGroupWizard({
  open,
  onOpenChange,
}: UseCreateManagedGroupWizardOptions) {
  const { t } = useTranslation("managedGroups");
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const shops = useAuthStore((state) => state.shops);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const workspaceShops = shops.filter((shop) => shop.workspaceId === user?.activeWorkspaceId);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [shopId, setShopId] = useState(currentShopId ?? workspaceShops[0]?.shopId ?? "");
  const [name, setName] = useState("");
  const [selectedCampaigns, setSelectedCampaigns] = useState<
    Map<string, AiManagedGroupEligibleCampaign>
  >(new Map());
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignDeliveryStatus, setCampaignDeliveryStatus] =
    useState<CandidateDeliveryFilter>("enabled");
  const [campaignPortfolioId, setCampaignPortfolioId] = useState<CandidatePortfolioFilter>("all");
  const [campaignAiStatus, setCampaignAiStatus] = useState<CandidateAiFilter>("all");
  const [campaignSortBy, setCampaignSortBy] = useState<CandidateSortField>("delivery");
  const [campaignSortDirection, setCampaignSortDirection] = useState<CandidateSortDirection>("asc");
  const [selectedCampaignPage, setSelectedCampaignPage] = useState(1);
  const [policy, setPolicy] = useState<AiManagedGroupPolicy>({
    businessGoal: "improve_efficiency",
    targetAcos: 0.3,
    riskPreference: "balanced",
    executionMode: "recommendation",
  });

  const excludedCampaignIds = [...selectedCampaigns.keys()].sort();
  const campaignsQuery = useQuery({
    queryKey: [
      "ai-managed-groups",
      "eligible-campaigns",
      shopId,
      campaignPage,
      campaignSearch,
      campaignDeliveryStatus,
      campaignPortfolioId,
      campaignAiStatus,
      campaignSortBy,
      campaignSortDirection,
      excludedCampaignIds,
    ],
    queryFn: () =>
      fetchEligibleManagedGroupCampaigns(shopId, {
        page: campaignPage,
        pageSize: CAMPAIGN_PAGE_SIZE,
        search: campaignSearch.trim(),
        status: campaignDeliveryStatus === "all" ? undefined : campaignDeliveryStatus,
        portfolioId: campaignPortfolioId === "all" ? undefined : campaignPortfolioId,
        aiStatus: campaignAiStatus === "all" ? undefined : campaignAiStatus,
        sortBy: campaignSortBy,
        sortDirection: campaignSortDirection,
        excludeCampaignIds: excludedCampaignIds,
      }),
    enabled: open && Boolean(shopId),
  });
  const portfoliosQuery = useQuery({
    queryKey: ["ai-managed-groups", "portfolio-options", shopId],
    queryFn: () => fetchManagedGroupPortfolioOptions(shopId),
    enabled: open && Boolean(shopId),
  });
  const systemQuery = useQuery({
    queryKey: ["settings", "ai-policy", shopId],
    queryFn: () => fetchSystemAiPolicyForShop(shopId),
    enabled: open && Boolean(shopId),
  });
  const diagnosticsLifecycleStatus = systemQuery.data?.systemEnabled ? "active" : "paused";
  const diagnosticsQueryKey = managedGroupDiagnosticsQueryKey(
    shopId,
    [...selectedCampaigns.keys()].sort(),
    policy,
    diagnosticsLifecycleStatus,
  );
  const diagnosticsQuery = useQuery({
    queryKey: diagnosticsQueryKey,
    queryFn: () =>
      fetchManagedGroupDiagnostics(shopId, {
        schemaVersion: 1,
        campaignIds: [...selectedCampaigns.keys()],
        policy,
        lifecycleStatus: diagnosticsLifecycleStatus,
      }),
    enabled:
      open &&
      step >= 2 &&
      Boolean(shopId) &&
      selectedCampaigns.size > 0 &&
      systemQuery.data !== undefined,
    // Policy-only edits may reuse the current scope's result; Shop, Campaign, and lifecycle
    // changes must wait for a fresh safety classification before Continue can be enabled.
    placeholderData: (previousData, previousQuery) =>
      canReuseManagedGroupDiagnosticsPlaceholder(previousQuery?.queryKey, diagnosticsQueryKey)
        ? keepPreviousData(previousData)
        : undefined,
  });

  useEffect(() => {
    const system = systemQuery.data;
    if (!system) return;
    setPolicy({
      businessGoal: system.policy.businessGoal,
      targetAcos: system.policy.targetAcos,
      riskPreference: system.policy.riskPreference,
      executionMode: resolveExecutionModeV1(system.policy),
    });
  }, [systemQuery.data]);

  const mutation = useMutation({
    mutationFn: (lifecycleStatus: "active" | "paused") => {
      const input: AiManagedGroupCreateInput = {
        name: name.trim(),
        campaignIds: [...selectedCampaigns.keys()],
        policy,
        lifecycleStatus,
      };
      return createManagedGroup(shopId, input);
    },
    onSuccess: async (group) => {
      toast.success(t("create.success", { name: group.name }));
      await queryClient.invalidateQueries({ queryKey: ["ai-managed-groups"] });
      reset();
      onOpenChange(false);
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  function reset(): void {
    setStep(1);
    setName("");
    setSelectedCampaigns(new Map());
    setCampaignPage(1);
    setCampaignSearch("");
    setCampaignDeliveryStatus("enabled");
    setCampaignPortfolioId("all");
    setCampaignAiStatus("all");
    setCampaignSortBy("delivery");
    setCampaignSortDirection("asc");
    setSelectedCampaignPage(1);
  }

  function changeShop(nextShopId: string | null): void {
    if (!nextShopId) return;
    setShopId(nextShopId);
    setSelectedCampaigns(new Map());
    setCampaignPage(1);
    setCampaignSearch("");
    setCampaignDeliveryStatus("enabled");
    setCampaignPortfolioId("all");
    setCampaignAiStatus("all");
    setCampaignSortBy("delivery");
    setCampaignSortDirection("asc");
    setSelectedCampaignPage(1);
  }

  function addCampaigns(campaignsToAdd: AiManagedGroupEligibleCampaign[]): void {
    setSelectedCampaigns((current) => {
      const next = new Map(current);
      for (const campaign of campaignsToAdd) {
        if (next.size >= 100) break;
        next.set(campaign.id, campaign);
      }
      return next;
    });
    setCampaignPage(1);
    setSelectedCampaignPage(1);
  }

  function removeCampaigns(campaignIds: string[]): void {
    setSelectedCampaigns((current) => {
      const next = new Map(current);
      for (const campaignId of campaignIds) next.delete(campaignId);
      return next;
    });
    setCampaignPage(1);
    setSelectedCampaignPage(1);
  }

  // Every candidate filter change restarts the available list at its first page.
  function changeCampaignSearch(value: string): void {
    setCampaignSearch(value);
    setCampaignPage(1);
  }

  function changeCampaignDeliveryStatus(value: CandidateDeliveryFilter): void {
    setCampaignDeliveryStatus(value);
    setCampaignPage(1);
  }

  function changeCampaignPortfolioId(value: CandidatePortfolioFilter): void {
    setCampaignPortfolioId(value);
    setCampaignPage(1);
  }

  function changeCampaignAiStatus(value: CandidateAiFilter): void {
    setCampaignAiStatus(value);
    setCampaignPage(1);
  }

  function changeCampaignSort(field: CandidateSortField): void {
    if (campaignSortBy === field) {
      setCampaignSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      setCampaignSortBy(field);
      setCampaignSortDirection("asc");
    }
    setCampaignPage(1);
  }

  const selectedCount = selectedCampaigns.size;
  const selectedValues = [...selectedCampaigns.values()];
  const alreadyAiOnCount = selectedValues.filter((campaign) => campaign.aiManaged).length;
  const canContinueScope = name.trim().length > 0 && selectedCount > 0;
  const canContinuePolicy =
    policy.targetAcos >= 0.01 &&
    policy.targetAcos <= 2 &&
    Boolean(diagnosticsQuery.data) &&
    diagnosticsQuery.data?.classification !== "blocked" &&
    !diagnosticsQuery.isError;
  const workspaceAiEnabled = systemQuery.data?.systemEnabled ?? false;
  const selectedShop = workspaceShops.find((shop) => shop.shopId === shopId);
  const shopItems = workspaceShops.map((shop) => ({ value: shop.shopId, label: shop.shopName }));
  const campaignPageData = campaignsQuery.data;
  const campaigns = campaignPageData?.data ?? [];
  const campaignTotal = campaignPageData?.pagination.total ?? 0;
  const campaignPageSize = campaignPageData?.pagination.pageSize ?? CAMPAIGN_PAGE_SIZE;

  return {
    step,
    setStep,
    shopId,
    name,
    setName,
    selectedCampaigns,
    campaignPage,
    setCampaignPage,
    campaignSearch,
    campaignDeliveryStatus,
    campaignPortfolioId,
    campaignAiStatus,
    campaignSortBy,
    campaignSortDirection,
    selectedCampaignPage,
    setSelectedCampaignPage,
    policy,
    setPolicy,
    campaignsQuery,
    portfoliosQuery,
    diagnosticsQuery,
    mutation,
    reset,
    changeShop,
    addCampaigns,
    removeCampaigns,
    changeCampaignSearch,
    changeCampaignDeliveryStatus,
    changeCampaignPortfolioId,
    changeCampaignAiStatus,
    changeCampaignSort,
    selectedCount,
    alreadyAiOnCount,
    canContinueScope,
    canContinuePolicy,
    workspaceAiEnabled,
    selectedShop,
    shopItems,
    campaigns,
    campaignTotal,
    campaignPageSize,
  };
}
